// ENG-3154: redesigned Sent record status page, gated by `sent_status_redesign`.
// Frontend only -- it renders the same payload as the legacy sentRecordStatus.jsx.
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import moment from 'moment-timezone';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  StopOutlined,
} from '@ant-design/icons';

import { verifyFeatureAccess } from '../../global/featureFlags';
import './sentRecordStatusRedesign.scss';

const ACCESS_LABELS = {
  can_view_and_send: 'View and send',
  can_view: 'View only',
  revoked: 'Revoked',
};

// fax_status is the delivery state for every channel: fax webhooks write it for faxes,
// the email workers and Mailgun webhook (mail_controller#get_icon) write it for email.
const DELIVERY_STATUS = {
  success: { label: 'Delivered', tone: 'success', Icon: CheckCircleOutlined },
  inprogress: { label: 'Delivery pending', tone: 'pending', Icon: ClockCircleOutlined },
  partialsuccess: { label: 'Partially delivered', tone: 'failure', Icon: CloseCircleOutlined },
  failure: { label: 'Failed', tone: 'failure', Icon: CloseCircleOutlined },
  scheduled: { label: 'Scheduled', tone: 'pending', Icon: ClockCircleOutlined },
};

const formatTime = (value, timezone) => moment.tz(value, timezone).format('lll');

const orNA = (value) => value || 'N/A';

// Patient sends (?patient=true) carry no delivery state in this payload, so they get
// no pill rather than an unverified "Sent".
const deliveryStatus = (data) => {
  if (!data.fax_status) return null;
  return DELIVERY_STATUS[data.fax_status] || {
    label: data.fax_status[0].toUpperCase() + data.fax_status.substr(1),
    tone: 'success',
    Icon: CheckCircleOutlined,
  };
};

// The count only moves when someone opens the Medsender copy (secure link or access
// code). A fax read off the recipient's machine never reaches us, so say so.
export const viewCountHint = (sentAsFax) => (sentAsFax
  ? 'Digital views only. This counts when the recipient opens the record online with the access code. Reading the paper fax is not counted.'
  : 'Digital views only. This counts each time the recipient opens the secure link.');

// direct_fax transmits the document itself (cover sheet optional); any other fax
// transmits only an access-code page, so the document is read online.
const accessSummary = (data, isAtaFax, sentAsFax, isFax) => {
  if (isAtaFax) return 'Received at their fax machine. There is no digital copy to share.';
  if (data.record_status === 'revoked') return 'Access revoked. The recipient can no longer open this record.';
  if (sentAsFax) return 'The full document was faxed to the recipient.';
  if (isFax) return 'An access code was faxed. The recipient opens the record online with it.';
  return 'The recipient can open this record online.';
};

const SentRecordStatusRedesign = ({
  data, openModal, fileToken, patient,
}) => {
  const timezone = moment.tz.guess();
  const isAtaFax = data.is_ata_fax === true;
  // Not fax_status: email sends carry one too. `receiver` is receiver_fax ||
  // receiver_email; a patient send's phone number is an SMS, not a fax.
  const isFax = !patient && !String(data.receiver).includes('@');
  const sentAsFax = data.record_status === 'direct_fax';
  const isRevoked = data.record_status === 'revoked';
  const status = deliveryStatus(data);
  const StatusIcon = status && status.Icon;

  const handleRevoke = async () => {
    const canRevoke = await verifyFeatureAccess('revoke_record');
    if (canRevoke) {
      return openModal({ type: 'REVOKE_RECORDS', data: { recordId: fileToken, sendToken: data.send_token } });
    }
    return openModal({ type: 'RAISE_FLAG', featureFlag: 'revoke_record' });
  };

  const details = [
    { label: 'Patient', value: data.patient_name || 'Not Provided' },
    { label: 'Date of birth', value: data.patient_dob || 'Not Provided' },
    { label: 'Memo', value: orNA(data.memo) },
    // Separate rows, not "X of Y": a fax's transmitted pages include the cover sheet,
    // so the sent count can exceed the original record's.
    { label: 'Pages sent', value: orNA(data.total_pages_sent) },
    { label: 'Pages in original record', value: orNA(data.num_pages) },
  ];
  if (isFax) {
    details.push({
      label: 'Delivery completed',
      value: data.completed_at ? formatTime(data.completed_at, timezone) : 'Delivery pending',
    });
    details.push({
      label: 'Access expires',
      value: data.expiry ? formatTime(data.expiry, timezone) : 'N/A',
    });
  } else {
    details.push({ label: 'Access', value: ACCESS_LABELS[data.record_status] || 'N/A' });
  }

  // resent_at is oldest first; show newest first, with the original send last.
  const sends = [...(data.resent_at || [])].reverse();
  const receiverName = data.receiver_name;

  return (
    <div className="sent-status-redesign" data-testid="sent-status-redesign">
      <section className="sent-status-redesign__card sent-status-redesign__hero">
        <div>
          <p className="sent-status-redesign__eyebrow">Sent to</p>
          <h1 className="sent-status-redesign__recipient">{receiverName || data.receiver}</h1>
          {receiverName && <p className="sent-status-redesign__address">{data.receiver}</p>}
        </div>
        {status && (
          <span className={`sent-status-redesign__pill sent-status-redesign__pill--${status.tone}`}>
            <StatusIcon />
            {status.label}
          </span>
        )}
      </section>

      {isRevoked && (
        <div className="sent-status-redesign__banner" role="status">
          <ExclamationCircleOutlined />
          The recipient&#39;s access to view this record has been revoked.
        </div>
      )}

      {data.fax_error_message && (
        <div className="sent-status-redesign__banner sent-status-redesign__banner--error" role="alert">
          <CloseCircleOutlined />
          {data.fax_error_message}
        </div>
      )}

      <div className="sent-status-redesign__tiles">
        <section className="sent-status-redesign__card sent-status-redesign__tile">
          <p className="sent-status-redesign__label">Recipient access</p>
          <p className="sent-status-redesign__tile-body">{accessSummary(data, isAtaFax, sentAsFax, isFax)}</p>
        </section>
        {!isAtaFax && (
          <section className="sent-status-redesign__card sent-status-redesign__tile" data-testid="sent-status-view-count">
            <p className="sent-status-redesign__label">
              <EyeOutlined />
              Digital views
            </p>
            <p className="sent-status-redesign__count">
              {data.view_count ? data.view_count : 'Not viewed yet'}
            </p>
            <p className="sent-status-redesign__hint">{viewCountHint(sentAsFax || isFax)}</p>
          </section>
        )}
      </div>

      <section className="sent-status-redesign__card">
        <h2 className="sent-status-redesign__section-title">Details</h2>
        <dl className="sent-status-redesign__details">
          {details.map(({ label, value }) => (
            <div key={label} className="sent-status-redesign__row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="sent-status-redesign__card">
        <h2 className="sent-status-redesign__section-title">Send history</h2>
        <ol className="sent-status-redesign__history">
          <li>
            <span>Last sent</span>
            <span>{formatTime(data.date_sent, timezone)}</span>
          </li>
          {sends.map((date, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${date}-${index}`}>
              <span>{index === sends.length - 1 ? 'First sent' : 'Sent'}</span>
              <span>{formatTime(date, timezone)}</span>
            </li>
          ))}
        </ol>
      </section>

      {!isAtaFax && (
        <div className="sent-status-redesign__actions">
          <Button
            danger
            onClick={handleRevoke}
            disabled={isRevoked || patient}
            icon={<StopOutlined />}
          >
            Revoke access
          </Button>
        </div>
      )}
    </div>
  );
};

SentRecordStatusRedesign.defaultProps = {
  patient: false,
};

SentRecordStatusRedesign.propTypes = {
  data: PropTypes.shape({
    fax_status: PropTypes.string,
    fax_error_message: PropTypes.string,
    receiver: PropTypes.string.isRequired,
    receiver_name: PropTypes.string,
    patient_name: PropTypes.string,
    patient_dob: PropTypes.string,
    date_sent: PropTypes.string.isRequired,
    resent_at: PropTypes.array,
    completed_at: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    expiry: PropTypes.string,
    record_status: PropTypes.string,
    memo: PropTypes.string,
    total_pages_sent: PropTypes.string,
    num_pages: PropTypes.string,
    is_ata_fax: PropTypes.bool,
    view_count: PropTypes.number,
    send_token: PropTypes.string,
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  patient: PropTypes.bool,
  fileToken: PropTypes.string.isRequired,
};

export default SentRecordStatusRedesign;
